import { buildInFilterPredicates } from '@/contexts/shared/filters-predicate/FiltersPredicate.helpers'
import {
  ORGANIZATION_ADD_MENU_PERIMETER_TYPES,
  ORGANIZATION_PERIMETER_TYPES,
} from '@/contexts/organization/Organization.constants'
import type {
  OrganizationCreatedTag,
  OrganizationPerimetersFilters,
} from '@/contexts/organization/Organization.interfaces'
import type { Quota } from '@/contexts/quotas/Quotas.interfaces'
import { TAG_TYPE } from '@/contexts/perimeters/Perimeters.constants'
import { canCreateInternalPerimeter } from '@/contexts/perimeters/Perimeters.helpers'
import {
  PerimeterTypeWithTag,
  type PerimetersGroupedFilter,
  type PerimeterTreeSelectOption,
} from '@/contexts/perimeters/Perimeters.interfaces'
import {
  PerimetersOrgChartNodeType,
  type PerimetersOrgChartNode,
} from '@/contexts/perimeters/perimeters-org-chart/PerimetersOrgChartV2.interfaces'
import type { NdsFilterSelectOption } from '@/components/NewDesignSystem/NdsFilterSelect/NdsFilterSelect.interfaces'

export const getBddId = (nodeParentId: string): number => {
  return Number(nodeParentId.substring(1))
}

// A perimeter can appear several times in the chart: once in each tree/tag containing it,
// and once more under "My perimeters" when it is a direct perimeter access of the user
export const findNodeOccurrences = ({
  nodes,
  dbId,
  type,
}: {
  nodes: PerimetersOrgChartNode[]
  dbId: number
  type: PerimetersOrgChartNodeType
}): PerimetersOrgChartNode[] => nodes.filter((node) => node.db_id === dbId && node.type === type)

// The perimeter stays in other groups, and under "My perimeters" when it is a direct perimeter access
export const removePerimeterFromGroup = (
  nodes: PerimetersOrgChartNode[],
  perimeterNode: PerimetersOrgChartNode
): PerimetersOrgChartNode[] =>
  nodes.filter((node) => node.id !== perimeterNode.id || node.parentId !== perimeterNode.parentId)

export const buildCreatedTagNode = ({
  tag,
  parentTagId,
}: {
  tag: OrganizationCreatedTag
  parentTagId: number
}): PerimetersOrgChartNode => ({
  id: PerimetersOrgChartNodeType.TAG + tag.id,
  db_id: tag.id,
  parentId: PerimetersOrgChartNodeType.TAG + parentTagId,
  type: PerimetersOrgChartNodeType.TAG,
  identifier: tag.identifier,
  name: tag.name,
  weight: tag.weight,
  color: tag.color,
  edit_right: tag.edit_right,
  delete_right: tag.delete_right,
})

export const getDefaultOrganizationPerimetersFilters = (): OrganizationPerimetersFilters => ({
  parentIds: [],
  types: [],
})

export const buildPerimetersGroupedFilter = (filters: OrganizationPerimetersFilters): PerimetersGroupedFilter => ({
  types: buildInFilterPredicates(filters.types),
  parentPerimeterIds: buildInFilterPredicates(filters.parentIds),
})

export const getParentFilterOptions = (
  perimeterTagOptions: PerimeterTreeSelectOption[]
): NdsFilterSelectOption<number>[] =>
  perimeterTagOptions
    .filter((option) => option.type === TAG_TYPE)
    .map((option) => ({
      id: option.id,
      label: option.name,
      data: option.perimeterId,
    }))

export const getAvailableOrganizationPerimetersFilters = (
  filters: OrganizationPerimetersFilters,
  perimeterTagOptions: PerimeterTreeSelectOption[]
): OrganizationPerimetersFilters => {
  const availableParentIds = getParentFilterOptions(perimeterTagOptions).map((option) => option.data)

  return {
    parentIds: filters.parentIds.filter((parentId) => availableParentIds.includes(parentId)),
    types: filters.types.filter((type) => ORGANIZATION_PERIMETER_TYPES.includes(type)),
  }
}

export const getCreatablePerimeterTypes = ({
  isGlobalPilote,
  perimetersQuota,
}: {
  isGlobalPilote: boolean
  perimetersQuota: Quota | null
}): PerimeterTypeWithTag[] =>
  ORGANIZATION_ADD_MENU_PERIMETER_TYPES.filter(
    (type) => type !== PerimeterTypeWithTag.INTERNAL || canCreateInternalPerimeter({ isGlobalPilote, perimetersQuota })
  )
